import { useEffect, useMemo, useState } from 'react';
import { Box, Alert, Typography, Link as MuiLink, ToggleButton, ToggleButtonGroup, TextField, InputAdornment, CircularProgress, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { CalendarDays, ShieldPlus, BarChart3, MessageSquare, Smartphone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePatientAuth } from '../../contexts/PatientAuthContext';
import { apiErrorMessage } from '../../lib/errorMapping';
import { patientApi } from '../../lib/api';
import { extractData } from '../../hooks/useQueries';
import type { OtpChallenge } from '../../lib/api/modules/patient-portal';
import { normaliseIndianMobile } from './public-booking/bookingModel';
import {
  AuthScreenLayout, AuthEmailPassword, AuthSubmitButton, OrDivider,
  alertSx, ACCENT_BLUE, type Feature,
} from '../auth/authShared';

// Icons are static; the copy is read from the `auth` namespace at render time so the panel
// re-renders in the new language the moment the switcher on this card is used.
const FEATURE_KEYS = [
  { icon: CalendarDays,  key: 'upcomingAppointments' },
  { icon: ShieldPlus,    key: 'medicalRecords' },
  { icon: BarChart3,     key: 'prescriptions' },
  { icon: MessageSquare, key: 'messages' },
] as const;

type Method = 'email' | 'sms';

/**
 * Patient sign-in: email + password, or an SMS code to the mobile number bound to the account (medopd-docs 136 §7 M3).
 * The SMS path opens only an account that itself proved it holds the number; a number with no such account is told to
 * sign in with email and add the number under Profile — never matched to a patient record that merely shares the number
 * (medopd-docs 96).
 */
export function PatientLogin() {
  const { t } = useTranslation('auth');
  const { t: tp } = useTranslation('portal');
  const { signIn, adoptSession } = usePatientAuth();
  const [method, setMethod] = useState<Method>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SMS code
  const [mobile, setMobile] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const digits = normaliseIndianMobile(mobile);
  const mobileValid = /^[6-9]\d{9}$/.test(digits);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const features: Feature[] = useMemo(
    () => FEATURE_KEYS.map(({ icon, key }) => ({
      icon,
      title: t(`feature.${key}`),
      desc: t(`feature.${key}Desc`),
    })),
    [t],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t('error.enterBoth'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await signIn(email, password);
      // PatientAuthContext takes over routing on success.
    } catch (err) {
      // The backend replies in English. apiErrorMessage runs it through the shared mapping so a
      // patient reading Telugu gets a Telugu message rather than a raw server string.
      setError(apiErrorMessage(err, t('error.invalidCredentials')));
      setLoading(false);
    }
  };

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!mobileValid || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await patientApi.auth.otpSignInRequest(digits);
      if (res.error) throw new Error(res.error);
      const c = extractData<OtpChallenge>(res);
      if (!c?.challengeId) throw new Error(tp('pbSendFailed', 'We couldn’t send a code. Please try again.'));
      setChallenge(c);
      setCode('');
      setResendIn(c.resendAfterSeconds ?? 30);
    } catch (err) {
      // OTP refusals are written for patients (quota, wrong code, no bound account): shown as sent, not re-mapped.
      setError(err instanceof Error && err.message ? err.message : tp('pbSendFailed', 'We couldn’t send a code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (value: string) => {
    if (!challenge || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await patientApi.auth.otpSignInVerify(challenge.challengeId, digits, value);
      if (res.error) throw new Error(res.error);
      adoptSession(res);
      // PatientAuthContext takes over routing on success.
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : tp('pbVerifyFailed', 'We couldn’t check the code. Please try again.'));
      setLoading(false);
    }
  };

  return (
    <AuthScreenLayout
      heading={{ pre: t('patient.headingPre'), accent: t('patient.headingAccent') }}
      subtext={t('patient.subtext')}
      features={features}
      cardTitle={t('patient.portalTitle')}
      cardSubtitle={t('patient.title')}
    >
      <ToggleButtonGroup exclusive fullWidth size="small" value={method} sx={{ mb: 2 }}
        onChange={(_e, v: Method | null) => { if (v) { setMethod(v); setError(null); } }}
        aria-label={tp('signInMethod', 'How do you want to sign in?')}>
        <ToggleButton value="email" sx={{ textTransform: 'none', fontWeight: 600, gap: 1 }}>
          <Mail size={16} aria-hidden /> {tp('signInWithEmail', 'Email & password')}
        </ToggleButton>
        <ToggleButton value="sms" sx={{ textTransform: 'none', fontWeight: 600, gap: 1 }}>
          <Smartphone size={16} aria-hidden /> {tp('signInWithSms', 'SMS code')}
        </ToggleButton>
      </ToggleButtonGroup>

      {method === 'email' ? (
        <form onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}

          <AuthEmailPassword
            email={email}
            password={password}
            onEmail={setEmail}
            onPassword={setPassword}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Box component={Link} to="/patient/forgot-password" sx={{ color: ACCENT_BLUE, fontWeight: 600, textDecoration: 'none', fontSize: '0.88rem' }}>
              {t('action.forgotPassword')}
            </Box>
          </Box>

          <AuthSubmitButton disabled={loading} loading={loading} loadingLabel={t('action.signingIn')}>
            {t('action.signIn')}
          </AuthSubmitButton>
        </form>
      ) : !challenge ? (
        <form onSubmit={sendCode}>
          {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
            {tp('smsSignInIntro', 'We’ll text a 6-digit code to the mobile number on your ClinVea account.')}
          </Typography>
          <TextField label={tp('pbMobile', 'Mobile number')} value={mobile} fullWidth autoFocus disabled={loading}
            onChange={(e) => setMobile(e.target.value.replace(/[^\d\s+-]/g, '').slice(0, 16))}
            inputProps={{ inputMode: 'tel', autoComplete: 'tel-national' }}
            InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
            error={digits.length >= 10 && !mobileValid}
            helperText={digits.length >= 10 && !mobileValid ? tp('pbMobileInvalid', 'Enter a valid 10-digit mobile number.') : ' '}
            sx={{ mb: 1.5 }} />
          <AuthSubmitButton disabled={!mobileValid || loading} loading={loading} loadingLabel={tp('sendingCode', 'Sending…')}>
            {tp('pbSendCode', 'Send code')}
          </AuthSubmitButton>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (code.length === 6) void verifyCode(code); }}>
          {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
            {tp('pbCodeSent', 'Enter the code sent to +91 {{mobile}}.', { mobile: `${digits.slice(0, 5)} ${digits.slice(5)}` })}{' '}
            <MuiLink component="button" type="button" variant="body2" onClick={() => { setChallenge(null); setError(null); }}>
              {tp('pbChangeNumber', 'Change number')}
            </MuiLink>
          </Typography>
          <TextField label={tp('pbCode', '6-digit code')} value={code} fullWidth autoFocus disabled={loading}
            onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setCode(v); if (v.length === 6) void verifyCode(v); }}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, style: { letterSpacing: '0.5em', fontSize: 22 } }}
            sx={{ mb: 1.5 }} />
          <AuthSubmitButton disabled={code.length !== 6 || loading} loading={loading} loadingLabel={t('action.signingIn')}>
            {t('action.signIn')}
          </AuthSubmitButton>
          <Box sx={{ mt: 1.5, textAlign: 'center' }}>
            {resendIn > 0 ? (
              <Typography variant="body2" sx={{ color: '#64748b' }}>{tp('pbResendIn', 'Didn’t get it? You can resend in {{s}}s.', { s: resendIn })}</Typography>
            ) : (
              <Button size="small" onClick={() => void sendCode()} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : undefined}>
                {tp('pbResend', 'Resend code')}
              </Button>
            )}
          </Box>
        </form>
      )}

      <OrDivider />

      {/* Secondary entry point kept small so it doesn't compete with the primary flow. */}
      <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
        {t('patient.areYouStaff')}{' '}
        <MuiLink component={Link} to="/login" sx={{ color: '#94a3b8', textDecoration: 'underline', fontWeight: 600 }}>
          {t('patient.staffSignIn')}
        </MuiLink>
      </Typography>
    </AuthScreenLayout>
  );
}
